import { useEffect, useRef, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { AppWindow, CircleHelp } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import { useStickyTopPx } from 'app/frontend/base/hooks/useStickyTop'
import AssignmentExpectedEntitiesCount from './AssignmentExpectedEntitiesCount'
import AssignmentPaletteEntityButton from './AssignmentPaletteEntityButton'
import { AssignmentPaletteEntity } from './assignmentPaletteEntities'
import { getPaletteDragId } from './assignmentDnd'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import SpecificCharacterFilterMenu from './SpecificCharacterFilterMenu'
import useSpecificCharacterFilters, {
  getSourceCategoryEntries,
  groupPaletteEntitiesByCategory,
} from './useSpecificCharacterFilters'

type Props = {
  paletteEntities: AssignmentPaletteEntity[]
  assignmentUsageCountByIdentity: Record<string, number>
  focusedScopeIdentities: Set<string>
  focusedTargetLabel: string | null
  focusedAssignedCount?: number
  focusedExpectedCount?: number
  focusedSpellId?: number
  canEdit: boolean
  onAddEntity: (entity: RaidPlanAssignmentEntity) => void
  onOpenPopup: () => void
}

const DraggablePaletteEntity = ({
  entry,
  assignmentCount,
  showCount,
  highlighted,
  canEdit,
  onClick,
}: {
  entry: AssignmentPaletteEntity
  assignmentCount: number
  showCount: boolean
  highlighted: boolean
  canEdit: boolean
  onClick?: () => void
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: getPaletteDragId(entry.id),
    data: { entity: entry.entity },
    disabled: !canEdit,
  })
  const suppressClickRef = useRef(false)
  useEffect(() => {
    if (isDragging) suppressClickRef.current = true
  }, [isDragging])
  return (
    <AssignmentPaletteEntityButton
      ref={setNodeRef}
      label={entry.label}
      assignmentCount={assignmentCount}
      category={entry.category}
      disabled={!canEdit}
      highlighted={highlighted}
      showCount={showCount}
      className={cn('w-full', isDragging && 'opacity-50')}
      onClick={() => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false
          return
        }
        onClick?.()
      }}
      {...(canEdit ? attributes : {})}
      {...(canEdit ? listeners : {})}
    />
  )
}

const AssignmentEntitySidebar = ({
  paletteEntities,
  assignmentUsageCountByIdentity,
  focusedScopeIdentities,
  focusedTargetLabel,
  focusedAssignedCount,
  focusedExpectedCount,
  focusedSpellId,
  canEdit,
  onAddEntity,
  onOpenPopup,
}: Props) => {
  const { t } = useTranslation()
  const [openCategory, setOpenCategory] = useState('by_role')
  const stickyTopPx = useStickyTopPx() + 12
  const paletteEntitiesByCategory =
    groupPaletteEntitiesByCategory(paletteEntities)
  const specificCharacterEntries =
    paletteEntitiesByCategory.specific_character ?? []
  const hasUnfilteredSpecificCharacterEntries = specificCharacterEntries.some(
    (entry) => {
      const rankId = entry.specificCharacterFilter?.teamRankId
      return rankId !== null && rankId !== undefined
    },
  )
  const filterStorageKey =
    focusedSpellId === undefined
      ? 'team-plan-assignment-source-filters:sidebar'
      : `team-plan-assignment-source-filters:${focusedSpellId}`
  const {
    specificCharacterFilters,
    setSpecificCharacterFilters,
    availableTeamRanks,
    defaultEnabledTeamRankIds,
    enabledTeamRankIdSet,
    hasSpecificCharacterFilterOverrides,
    isSpecificCharacterVisible,
  } = useSpecificCharacterFilters(filterStorageKey, specificCharacterEntries)
  const sourceCategoryEntries = getSourceCategoryEntries(
    hasUnfilteredSpecificCharacterEntries,
    'sidebar',
  )
  return (
    <div
      className='w-72 shrink-0 sticky self-start max-h-[calc(100vh-8rem)]'
      style={{ top: stickyTopPx }}
    >
      <Card className='overflow-y-auto max-h-[calc(100vh-9rem)] p-0'>
        <div className='flex items-start justify-between gap-2 px-3 pt-3 pb-2'>
          <div className='min-w-0'>
            <div className='font-medium text-sm inline-flex items-center gap-2'>
              <span>{t('dashboard.team.plans.assignments.sidebar-title')}</span>
              {focusedTargetLabel ? (
                <AssignmentExpectedEntitiesCount
                  actual={focusedAssignedCount ?? 0}
                  expected={focusedExpectedCount}
                />
              ) : null}
            </div>
            <p className='text-sm text-muted-foreground mt-0.5'>
              {focusedTargetLabel
                ? t('dashboard.team.plans.assignments.sidebar-focus-hint', {
                    target: focusedTargetLabel,
                  })
                : t('dashboard.team.plans.assignments.sidebar-drag-hint')}
            </p>
          </div>
          {focusedTargetLabel ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='h-7 w-7 shrink-0'
                  onClick={onOpenPopup}
                >
                  <AppWindow className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('dashboard.team.plans.assignments.assign-in-popup')}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to='/support/events/raid-plan-assignments'
                  className={cn(
                    buttonVariants({ variant: 'ghost', size: 'icon' }),
                    'h-7 w-7 shrink-0',
                  )}
                >
                  <CircleHelp className='h-4 w-4' />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                {t('dashboard.team.plans.assignments.sidebar-help')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <Accordion
          type='single'
          collapsible
          value={openCategory}
          onValueChange={setOpenCategory}
          className='w-full border-t'
        >
          {sourceCategoryEntries.map(([category, titleKey]) => {
            const sortedCategoryEntities = (
              paletteEntitiesByCategory[category] ?? []
            ).filter((entry) => {
              if (category !== 'specific_character') return true
              return isSpecificCharacterVisible(entry)
            })
            if (
              category !== 'specific_character' &&
              !sortedCategoryEntities.length
            ) {
              return null
            }
            return (
              <AccordionItem
                key={category}
                value={category}
                className='border-b last:border-b-0 [&>h3]:m-0'
              >
                <AccordionTrigger className='px-3 py-2 text-sm hover:no-underline'>
                  <span className='inline-flex items-center gap-1'>
                    <span>{t(titleKey)}</span>
                    {category === 'specific_character' ? (
                      <span
                        className='inline-flex items-center gap-1'
                        onClick={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex'>
                              <CircleHelp className='h-3.5 w-3.5' />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t(
                              'dashboard.team.plans.assignments.categories.specific-character-tooltip',
                            )}
                          </TooltipContent>
                        </Tooltip>
                        <SpecificCharacterFilterMenu
                          filters={specificCharacterFilters}
                          onFiltersChange={setSpecificCharacterFilters}
                          availableTeamRanks={availableTeamRanks}
                          defaultEnabledTeamRankIds={defaultEnabledTeamRankIds}
                          enabledTeamRankIdSet={enabledTeamRankIdSet}
                          hasOverrides={hasSpecificCharacterFilterOverrides}
                        />
                      </span>
                    ) : null}
                  </span>
                </AccordionTrigger>
                <AccordionContent className='px-3 pb-1.5'>
                  {sortedCategoryEntities.length === 0 &&
                  category === 'specific_character' ? (
                    <div className='text-sm text-muted-foreground'>
                      {t(
                        'dashboard.team.plans.assignments.categories.specific-character-empty',
                      )}
                    </div>
                  ) : (
                    <div className='grid grid-cols-2 gap-1'>
                      {sortedCategoryEntities.map((entry) => {
                        const entryIdentity = getAssignmentEntityIdentity(
                          entry.entity,
                        )
                        const isAlreadyAdded =
                          entry.category !== 'by_role' &&
                          entry.category !== 'by_class' &&
                          focusedScopeIdentities.has(entryIdentity)
                        return (
                          <DraggablePaletteEntity
                            key={entry.id}
                            entry={entry}
                            assignmentCount={
                              assignmentUsageCountByIdentity[entryIdentity] ?? 0
                            }
                            showCount={Boolean(focusedTargetLabel)}
                            highlighted={Boolean(
                              focusedTargetLabel && isAlreadyAdded,
                            )}
                            canEdit={canEdit}
                            onClick={
                              focusedTargetLabel
                                ? () => onAddEntity(entry.entity)
                                : undefined
                            }
                          />
                        )
                      })}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            )
          })}
        </Accordion>
      </Card>
    </div>
  )
}

export default AssignmentEntitySidebar
